import type {
  GeneralConfig,
  FirebaseConfig,
  GoogleConfig,
  EmailConfig,
  OpenAIConfig,
  ResendConfig,
  StorageConfig,
  GrafanaConfig
} from './types';
import {
  DEFAULT_GENERAL_CONFIG,
  DEFAULT_FIREBASE_CONFIG,
  DEFAULT_GOOGLE_CONFIG,
  DEFAULT_EMAIL_CONFIG,
  DEFAULT_OPENAI_CONFIG,
  DEFAULT_RESEND_CONFIG,
  DEFAULT_STORAGE_CONFIG,
  DEFAULT_GRAFANA_CONFIG
} from './constants';

export interface DbConfigMappingResult {
  general?: GeneralConfig;
  firebase?: FirebaseConfig;
  google?: GoogleConfig;
  email?: Partial<EmailConfig>;
  openai?: OpenAIConfig;
  resend?: ResendConfig;
}

export function mapDbConfigToState(dbConfig: any): DbConfigMappingResult {
  const result: DbConfigMappingResult = {};

  if (dbConfig.general) {
    result.general = {
      companyName: dbConfig.general.companyName || DEFAULT_GENERAL_CONFIG.companyName,
      supportEmail: dbConfig.general.supportEmail || DEFAULT_GENERAL_CONFIG.supportEmail,
      systemTimezone: dbConfig.general.systemTimezone || DEFAULT_GENERAL_CONFIG.systemTimezone,
      defaultCurrency: DEFAULT_GENERAL_CONFIG.defaultCurrency
    };
  }

  if (dbConfig.firebase) {
    result.firebase = {
      enabled: dbConfig.firebase.isActive !== false,
      apiKey: dbConfig.firebase.apiKey || '',
      authDomain: dbConfig.firebase.authDomain || '',
      projectId: dbConfig.firebase.projectId || '',
      storageBucket: dbConfig.firebase.storageBucket || '',
      messagingSenderId: dbConfig.firebase.messagingSenderId || '',
      appId: dbConfig.firebase.appId || '',
      vapidKey: dbConfig.firebase.vapidKey || ''
    };
  }

  if (dbConfig.google) {
    result.google = {
      enabled: dbConfig.google.isActive !== false,
      clientId: dbConfig.google.clientId || '',
      clientSecret: dbConfig.google.clientSecret || '',
      redirectUri: dbConfig.google.redirectUri || DEFAULT_GOOGLE_CONFIG.redirectUri
    };
  }

  if (dbConfig.sendgrid) {
    result.email = {
      enabled: dbConfig.sendgrid.isActive !== false,
      sendgridApiKey: dbConfig.sendgrid?.apiKey || ''
    };
  }

  if (dbConfig.openai) {
    result.openai = {
      enabled: dbConfig.openai.isActive !== false,
      apiKey: dbConfig.openai.apiKey || '',
      organizationId: dbConfig.openai.organizationId || '',
      model: dbConfig.openai.model || DEFAULT_OPENAI_CONFIG.model,
      maxTokens: dbConfig.openai.maxTokens || DEFAULT_OPENAI_CONFIG.maxTokens,
      temperature: dbConfig.openai.temperature || DEFAULT_OPENAI_CONFIG.temperature,
      isConfigured: !!dbConfig.openai.apiKey,
      billingInfo: null,
      usageHistory: []
    };
  }

  if (dbConfig.resend) {
    result.resend = {
      enabled: dbConfig.resend.isActive !== false,
      apiKey: dbConfig.resend.apiKey || '',
      fromEmail: dbConfig.resend.fromEmail || DEFAULT_RESEND_CONFIG.fromEmail,
      fromName: dbConfig.resend.fromName || DEFAULT_RESEND_CONFIG.fromName,
      isConfigured: dbConfig.resend.isConfigured || false
    };
  }

  return result;
}

export interface LocalStorageMappingResult {
  general: GeneralConfig;
  firebase: FirebaseConfig;
  google: GoogleConfig;
  email: EmailConfig;
  openai: OpenAIConfig;
  resend: ResendConfig;
  grafana: GrafanaConfig;
}

export function mapLocalStorageToState(config: any): LocalStorageMappingResult {
  return {
    general: {
      companyName: config.companyName || DEFAULT_GENERAL_CONFIG.companyName,
      supportEmail: config.supportEmail || DEFAULT_GENERAL_CONFIG.supportEmail,
      systemTimezone: config.systemTimezone || DEFAULT_GENERAL_CONFIG.systemTimezone,
      defaultCurrency: config.defaultCurrency || DEFAULT_GENERAL_CONFIG.defaultCurrency
    },
    firebase: {
      enabled: DEFAULT_FIREBASE_CONFIG.enabled,
      apiKey: config.firebaseApiKey || '',
      authDomain: config.firebaseAuthDomain || '',
      projectId: config.firebaseProjectId || '',
      storageBucket: config.firebaseStorageBucket || '',
      messagingSenderId: config.firebaseMessagingSenderId || '',
      appId: config.firebaseAppId || '',
      vapidKey: config.firebaseVapidKey || ''
    },
    google: {
      enabled: DEFAULT_GOOGLE_CONFIG.enabled,
      clientId: config.googleClientId || '',
      clientSecret: config.googleClientSecret || '',
      redirectUri: config.googleRedirectUri || DEFAULT_GOOGLE_CONFIG.redirectUri
    },
    email: {
      enabled: DEFAULT_EMAIL_CONFIG.enabled,
      useInternalEmail: config.useInternalEmail || false,
      sendgridApiKey: config.sendgridApiKey || '',
      smtpHost: config.smtpHost || '',
      smtpPort: config.smtpPort || DEFAULT_EMAIL_CONFIG.smtpPort,
      smtpSecure: config.smtpSecure !== false,
      smtpUser: config.smtpUser || '',
      smtpPassword: config.smtpPassword || '',
      emailFromAddress: config.emailFromAddress || DEFAULT_EMAIL_CONFIG.emailFromAddress,
      emailFromName: config.emailFromName || DEFAULT_EMAIL_CONFIG.emailFromName
    },
    openai: {
      enabled: DEFAULT_OPENAI_CONFIG.enabled,
      apiKey: config.openaiApiKey || '',
      organizationId: config.openaiOrganizationId || '',
      model: config.openaiModel || DEFAULT_OPENAI_CONFIG.model,
      maxTokens: config.openaiMaxTokens || DEFAULT_OPENAI_CONFIG.maxTokens,
      temperature: config.openaiTemperature || DEFAULT_OPENAI_CONFIG.temperature,
      isConfigured: config.openaiIsConfigured || false,
      billingInfo: null,
      usageHistory: []
    },
    resend: {
      enabled: DEFAULT_RESEND_CONFIG.enabled,
      apiKey: config.resendApiKey || '',
      fromEmail: config.resendFromEmail || DEFAULT_RESEND_CONFIG.fromEmail,
      fromName: config.resendFromName || DEFAULT_RESEND_CONFIG.fromName,
      isConfigured: config.resendIsConfigured || false
    },
    grafana: {
      enabled: config.grafanaEnabled || false,
      url: config.grafanaUrl || '',
      apiKey: config.grafanaApiKey || '',
      lokiUrl: config.grafanaLokiUrl || '',
      dashboardId: config.grafanaDashboardId || '',
      isConfigured: config.grafanaIsConfigured || false
    }
  };
}

export function mapStorageConnectionToState(storageConnection: any): StorageConfig {
  return {
    serviceType: 'wasabi',
    endpoint: storageConnection.config.endpoint || '',
    accessKey: storageConnection.config.access_key || '',
    secretKey: storageConnection.config.secret_key || '',
    bucketName: storageConnection.config.bucket_name || '',
    region: storageConnection.config.region || '',
    isConfigured: storageConnection.is_active,
    lastBackup: null,
    backupFrequency: DEFAULT_STORAGE_CONFIG.backupFrequency
  };
}

export function mapStateToSystemConfig(state: {
  generalConfig: GeneralConfig;
  firebaseConfig: FirebaseConfig;
  googleConfig: GoogleConfig;
  emailConfig: EmailConfig;
  openaiConfig: OpenAIConfig;
  resendConfig: ResendConfig;
  grafanaConfig: GrafanaConfig;
}) {
  return {
    general: {
      companyName: state.generalConfig.companyName,
      supportEmail: state.generalConfig.supportEmail,
      systemTimezone: state.generalConfig.systemTimezone
    },
    firebase: {
      apiKey: state.firebaseConfig.apiKey,
      authDomain: state.firebaseConfig.authDomain,
      projectId: state.firebaseConfig.projectId,
      storageBucket: state.firebaseConfig.storageBucket,
      messagingSenderId: state.firebaseConfig.messagingSenderId,
      appId: state.firebaseConfig.appId,
      vapidKey: state.firebaseConfig.vapidKey,
      isActive: state.firebaseConfig.enabled
    },
    google: {
      clientId: state.googleConfig.clientId,
      clientSecret: state.googleConfig.clientSecret,
      redirectUri: state.googleConfig.redirectUri,
      isActive: state.googleConfig.enabled
    },
    sendgrid: {
      apiKey: state.emailConfig.sendgridApiKey,
      isActive: state.emailConfig.enabled
    },
    openai: {
      apiKey: state.openaiConfig.apiKey,
      organizationId: state.openaiConfig.organizationId,
      model: state.openaiConfig.model,
      maxTokens: state.openaiConfig.maxTokens,
      temperature: state.openaiConfig.temperature,
      isActive: state.openaiConfig.enabled
    },
    resend: {
      apiKey: state.resendConfig.apiKey,
      isConfigured: state.resendConfig.isConfigured,
      fromEmail: state.resendConfig.fromEmail,
      fromName: state.resendConfig.fromName,
      isActive: state.resendConfig.enabled
    },
    grafana: {
      enabled: state.grafanaConfig.enabled,
      url: state.grafanaConfig.url,
      apiKey: state.grafanaConfig.apiKey,
      lokiUrl: state.grafanaConfig.lokiUrl,
      dashboardId: state.grafanaConfig.dashboardId,
      isConfigured: state.grafanaConfig.isConfigured,
      isActive: state.grafanaConfig.enabled
    }
  };
}

export function calculateTotalOpenAICost(usageHistory: any[]): number {
  return usageHistory.reduce((sum: number, usage: any) => sum + usage.estimatedCost, 0);
}
